import { graphql, useFragment } from 'react-relay';

import {
    CUSTOMER_SUPPORT_URL,
    UNASSIGNED_REP_EMAIL,
    SILVER_PLUS_CODES,
    TRADE_SALES_EMAIL,
    type TradeStatusCode,
} from './tradeBenefitsConstants.ts';

import { type useTradeBenefitsContact_viewer$key } from './__generated__/useTradeBenefitsContact_viewer.graphql.ts';
import { type useTradeBenefitsContact_tradeFirm$key } from './__generated__/useTradeBenefitsContact_tradeFirm.graphql.ts';

const viewerFragment = graphql`
    fragment useTradeBenefitsContact_viewer on Viewer {
        tradeRep: userRelationships(userIds: [$userId], relationshipType: TRADE) {
            userDataB: userDataBRep {
                displayName
                firstName
                email
                photoUrl
                phoneNumber(formatType: INTERNATIONAL)
                isSystemUser
            }
        }
    }
`;

const tradeFirmFragment = graphql`
    fragment useTradeBenefitsContact_tradeFirm on TradeFirmType {
        rewards {
            currentProgram {
                programInfo {
                    code
                }
            }
        }
    }
`;

type TradeBenefitsContact = {
    isSilverPlus: boolean;
    isAssigned: boolean;
    displayName: string | null | undefined;
    firstName: string | null | undefined;
    repEmail: string | null | undefined;
    photoUrl: string | null | undefined;
    phoneNumber: string | null | undefined;
};

export const useTradeBenefitsContact = ({
    viewer: viewerRef,
    tradeFirm: tradeFirmRef,
}: {
    viewer: useTradeBenefitsContact_viewer$key;
    tradeFirm: useTradeBenefitsContact_tradeFirm$key | null | undefined;
}): TradeBenefitsContact => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const tradeFirm = useFragment(tradeFirmFragment, tradeFirmRef);
    const programCode = tradeFirm?.rewards?.currentProgram?.programInfo?.code;

    const {
        displayName,
        firstName,
        email: repEmail,
        isSystemUser,
        photoUrl,
        phoneNumber,
    } = viewer?.tradeRep?.[0]?.userDataB?.[0] || {};

    const isSilverPlus = SILVER_PLUS_CODES.has(programCode as TradeStatusCode);
    const isAssigned = !!repEmail && repEmail !== UNASSIGNED_REP_EMAIL && !isSystemUser;

    return { isSilverPlus, isAssigned, displayName, firstName, repEmail, photoUrl, phoneNumber };
};

export const getContactUrl = ({
    isAssigned,
    isSilverPlus,
    repEmail,
}: {
    isAssigned: boolean;
    isSilverPlus: boolean;
    repEmail: string | null | undefined;
}): string => {
    if (isAssigned && repEmail) {
        return `mailto:${repEmail}`;
    }

    if (isSilverPlus) {
        return `mailto:${TRADE_SALES_EMAIL}`;
    }

    return CUSTOMER_SUPPORT_URL;
};
